import { ActionCode } from "@/constants/action-codes";
import { PermissionCode } from "@/constants/permissions";
import { BlockedAction, getBlockedActions } from "@/services/actions.service";
import { useCallback, useEffect, useState } from "react";

const MAX_ATTEMPTS = 2;

export const useBlockedActions = (codigoPermiso: PermissionCode, isActive: boolean = true) => {
  const [blockedActions, setBlockedActions] = useState<BlockedAction[]>([]);
  const [loadingBlockedActions, setLoadingBlockedActions] = useState<boolean>(false);
  const [blockedActionsError, setBlockedActionsError] = useState<unknown>(null);

  useEffect(() => {
    if (!isActive) return;

    let cancelled = false;

    const fetchBlockedActions = async () => {
      setLoadingBlockedActions(true);
      setBlockedActionsError(null);

      let lastError: unknown = null;

      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        try {
          const response = await getBlockedActions(codigoPermiso);
          if (cancelled) return;

          setBlockedActions(response ?? []);
          setLoadingBlockedActions(false);
          return;
        } catch (error) {
          lastError = error;
        }
      }

      if (cancelled) return;
      setBlockedActionsError(lastError);
      setLoadingBlockedActions(false);
    };

    fetchBlockedActions();

    return () => {
      cancelled = true;
    };
  }, [codigoPermiso, isActive]);

  const isActionBlocked = useCallback(
    (actionCode: ActionCode) => {
      // Fail-closed: mientras no tengamos una respuesta vigente (todavía no está
      // activo, o hay un fetch en vuelo), tratamos la acción como bloqueada para
      // evitar el flash de botones habilitados que después se ocultan/deshabilitan.
      if (!isActive || loadingBlockedActions) return true;

      return blockedActions.find((action) => action.codigo === actionCode)?.bloqueada ?? false;
    },
    [blockedActions, loadingBlockedActions, isActive],
  );

  return {
    blockedActions,
    loadingBlockedActions,
    blockedActionsError,
    isActionBlocked,
  };
};
